"use client";

import { useSubscription } from "@/components/subscription-provider";
import { Button } from "@/components/ui/button";
import { PublisherAvatar } from "@/components/ui/user-avatar";
import PublisherConfig from "@/configs/publisher.json";
import { useTranslation } from "@/i18n";
import { hasAuthToken } from "@/lib/auth-cookie";
import { SITE_SHELL } from "@/lib/layout";
import { Mail } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

interface SiteHeaderProps {
  authVersion?: number;
  onOpenAccount?: () => void;
}

export function SiteHeader({
  authVersion = 0,
  onOpenAccount,
}: SiteHeaderProps) {
  const { t } = useTranslation();
  const [signedIn, setSignedIn] = useState(false);
  const { isSubscribed, isSiteOwner, isLoading } = useSubscription();

  useEffect(() => {
    setSignedIn(hasAuthToken());
  }, [authVersion]);

  const handleSubscribe = () => {
    window.dispatchEvent(new CustomEvent("open-subscribe-modal"));
  };

  const showManageSubscriptions = signedIn && isSubscribed && !isSiteOwner;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div
        className={`${SITE_SHELL} h-[var(--height-topbar,52px)] flex items-center justify-between gap-3`}
      >
        <Link
          href="/"
          className="flex items-center gap-2 min-w-0 hover:opacity-90 transition-opacity"
          aria-label={`${PublisherConfig.profile.name} home`}
        >
          <PublisherAvatar
            className="w-7 h-7 shrink-0 border border-border/70"
            fallbackClassName="text-[10px]"
          />
          <span className="text-sm font-semibold text-foreground truncate">
            {PublisherConfig.profile.name}
          </span>
        </Link>

        {isLoading ? (
          <div
            role="status"
            aria-label="Loading subscription"
            className="h-8 w-32 rounded-md bg-muted animate-pulse shrink-0"
          />
        ) : isSiteOwner ? null : (
          <div className="flex items-center gap-2 shrink-0">
            {showManageSubscriptions ? (
              <Button
                type="button"
                size="sm"
                onClick={onOpenAccount}
                className="cursor-pointer"
              >
                <span data-i18n="subscribe.manage">
                  {t("subscribe.manage")}
                </span>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={handleSubscribe}
                className="cursor-pointer"
              >
                <Mail className="w-3.5 h-3.5" />
                <span data-i18n="subscribe.subscribe">
                  {t("subscribe.subscribe")}
                </span>
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
